import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useDebounce } from "minimal-shared";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid2,
  TextField,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  Typography,
  IconButton,
  CircularProgress,
  Link as MuiLink,
} from "@mui/material";
import { Iconify } from "../../../Minimal/src/components/iconify/iconify";
import config from "../../../common/Config";
import CustomerOrderHistoryPopup from "./CustomerOrderHistoryPopup";
import CustomerLedgerEnquiryPopup from "./CustomerLedgerEnquiryPopup";
import QuickOrderCustomerEditPopup from "./QuickOrderCustomerEditPopup";
import "../../../common/commonStyles.css";

const blackInputSx = {
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "#000",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "#000",
  },
  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "#000",
  },
  "& .MuiInputLabel-root": {
    color: "#000",
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: "#000",
  },
};

const headerCellSx = {
  color: "#000",
  fontWeight: 700,
  backgroundColor: "#f4f6f8",
};

/**
 * Standalone customer search popup for Quick Order.
 * Kept fully separate from the existing CustomerSearch / getCustomerList
 * so it does not touch the already-working inline customer search.
 */
const fetchCustomerListForPopup = async ({ customerName, accountNo, postcode }) => {
  const accessToken = localStorage.getItem("accessToken") || config.PREFIX || "rhp";
  const formData = new FormData();
  formData.append("platform", "web");
  formData.append("pattern", customerName || "");
  formData.append("customer_name", customerName || "");
  formData.append("account_no", accountNo || "");
  formData.append("account_number", accountNo || "");
  formData.append("postcode", postcode || "");
  formData.append("postal_code", postcode || "");
  formData.append("authorization", `Bearer ${accessToken}`);

  const response = await fetch(`${config.API_URL}/ws-get-customer-list-pos`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
    body: formData,
  });
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  return response.json();
};

const getPopupCustomerAccountNo = (customer) => {
  if (!customer) return "";
  const raw =
    customer.accound_no ??
    customer.account_no ??
    customer.accountNo ??
    customer.account_number ??
    "";
  return String(raw).trim();
};

const getPopupCustomerPostcode = (customer) => {
  if (!customer) return "";
  const raw =
    customer.postcode ??
    customer.post_code ??
    customer.postal_code ??
    customer.zip_code ??
    customer.zipcode ??
    customer.pincode ??
    "";
  return String(raw).trim();
};

/** UK-style address: line(s), city, county ("state"), postcode. */
const getPopupCustomerFullAddress = (customer) => {
  if (!customer) return "";
  // `address` is the complete address string returned by this API and is used as-is
  // elsewhere (see quick-order.jsx / NewEpos.jsx `selectedCustomer.address`), so prefer
  // it over the shorter `line1`-style fields, which this endpoint may leave partial.
  const line1 = customer.address || customer.line1 || customer.addressLine1 || customer.address_line1 || "";
  const line2 = customer.line2 || customer.addressLine2 || customer.address_line2 || "";
  const city = customer.city || "";
  const county = customer.state || customer.county || "";
  const postcode = getPopupCustomerPostcode(customer);
  return [line1, line2, city, county, postcode]
    .map((part) => String(part || "").trim())
    .filter(Boolean)
    .join(", ");
};

const getPopupCustomerAccountBalance = (customer) => {
  if (!customer) return null;
  const raw =
    customer.outstanding_amount ??
    customer.account_balance ??
    customer.current_outstanding ??
    customer.balance ??
    customer.prev_due ??
    customer.amtDue;
  if (raw === null || raw === undefined || raw === "") return null;
  const num = Number(String(raw).replace(/,/g, ""));
  return Number.isNaN(num) ? null : num;
};

const formatPopupBalance = (value) => {
  if (value === null || value === undefined) return "-";
  return Number(value).toLocaleString("en-GB", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const CustomerListPopup = ({ open, onClose }) => {
  const isTelecaller = String(localStorage.getItem("user_role") || "").trim() === "20";
  const [customerName, setCustomerName] = useState("");
  const [accountNo, setAccountNo] = useState("");
  const [postcode, setPostcode] = useState("");
  const [historyCustomer, setHistoryCustomer] = useState(null);
  const [ledgerCustomer, setLedgerCustomer] = useState(null);
  const [editCustomer, setEditCustomer] = useState(null);

  const debouncedCustomerName = useDebounce(customerName, 400);
  const debouncedAccountNo = useDebounce(accountNo, 400);
  const debouncedPostcode = useDebounce(postcode, 400);

  const hasSearch =
    debouncedCustomerName.trim().length >= 2 ||
    debouncedAccountNo.trim().length >= 1 ||
    debouncedPostcode.trim().length >= 2;

  const { data, isLoading, isFetching } = useQuery({
    queryKey: [
      "quickOrderCustomerListPopup",
      debouncedCustomerName,
      debouncedAccountNo,
      debouncedPostcode,
    ],
    queryFn: () =>
      fetchCustomerListForPopup({
        customerName: debouncedCustomerName.trim(),
        accountNo: debouncedAccountNo.trim(),
        postcode: debouncedPostcode.trim(),
      }),
    enabled: open && hasSearch,
    staleTime: 1000 * 60,
  });

  const customers = data?.customer_details || [];

  const resetAndClose = () => {
    setCustomerName("");
    setAccountNo("");
    setPostcode("");
    setHistoryCustomer(null);
    setLedgerCustomer(null);
    setEditCustomer(null);
    onClose();
  };

  const handleAccountNoClick = (event, customer) => {
    event.stopPropagation();
    setHistoryCustomer(customer);
  };

  const handleBalanceClick = (event, customer) => {
    event.stopPropagation();
    setLedgerCustomer(customer);
  };

  const handleCustomerSelect = (customer) => {
    if (!isTelecaller || !customer?.id) return;
    setEditCustomer(customer);
  };

  return (
    <Dialog open={open} onClose={resetAndClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        Customer List
        <IconButton onClick={resetAndClose} size="small">
          <Iconify icon="mdi:close" width={20} height={20} />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Grid2 container spacing={1.5} sx={{ mb: 2, mt: 0.5 }}>
          <Grid2 size={{ xs: 12, sm: 4 }}>
            <TextField
              className="input-border"
              fullWidth
              size="small"
              label="Search Customer Name"
              placeholder="Search Customer Name"
              value={customerName}
              onChange={(e) => setCustomerName(e.target.value)}
              autoFocus
              sx={blackInputSx}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 4 }}>
            <TextField
              className="input-border"
              fullWidth
              size="small"
              label="Search A/C Number"
              placeholder="Search A/C Number"
              value={accountNo}
              onChange={(e) => setAccountNo(e.target.value)}
              sx={blackInputSx}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 4 }}>
            <TextField
              className="input-border"
              fullWidth
              size="small"
              label="Search Post Code"
              placeholder="Search Post Code"
              value={postcode}
              onChange={(e) => setPostcode(e.target.value)}
              sx={blackInputSx}
            />
          </Grid2>
        </Grid2>

        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 420 }}>
          <Table stickyHeader size="small" sx={{ tableLayout: "fixed" }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: "12%", ...headerCellSx }}>A/C No</TableCell>
                <TableCell sx={{ width: "24%", ...headerCellSx }}>Customer Name</TableCell>
                <TableCell sx={{ width: "12%", ...headerCellSx }}>Postcode</TableCell>
                <TableCell sx={{ width: "40%", ...headerCellSx }}>Address</TableCell>
                <TableCell align="right" sx={{ width: "12%", ...headerCellSx }}>A/c Balance</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!hasSearch && (
                <TableRow>
                  <TableCell colSpan={5}>
                    <Typography variant="body2" color="text.secondary" align="center">
                      Enter Search Customer Name, Search A/C Number or Search Post Code
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {hasSearch && (isLoading || isFetching) && (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <CircularProgress size={20} />
                  </TableCell>
                </TableRow>
              )}
              {hasSearch && !isLoading && !isFetching && customers.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5}>
                    <Typography variant="body2" color="text.secondary" align="center">
                      No customers found
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {hasSearch &&
                customers.map((customer) => {
                  const balance = getPopupCustomerAccountBalance(customer);
                  return (
                  <TableRow
                    key={customer.id}
                    hover
                  >
                    <TableCell>
                      <MuiLink
                        component="button"
                        underline="always"
                        onClick={(event) => handleAccountNoClick(event, customer)}
                        sx={{
                          cursor: "pointer",
                          fontSize: "0.8rem",
                          color: "#212b36",
                          textDecoration: "underline !important",
                        }}
                      >
                        {getPopupCustomerAccountNo(customer) || "-"}
                      </MuiLink>
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: "0.8rem",
                        whiteSpace: "normal",
                        wordBreak: "break-word",
                        ...(isTelecaller ? { cursor: "pointer", textDecoration: "underline" } : {}),
                      }}
                      onClick={() => handleCustomerSelect(customer)}
                    >
                      {customer.company || customer.name || ""}
                    </TableCell>
                    <TableCell sx={{ fontSize: "0.8rem" }}>{getPopupCustomerPostcode(customer)}</TableCell>
                    <TableCell sx={{ fontSize: "0.8rem", whiteSpace: "normal", wordBreak: "break-word" }}>
                      {getPopupCustomerFullAddress(customer)}
                    </TableCell>
                    <TableCell align="right">
                      <MuiLink
                        component="button"
                        underline="always"
                        onClick={(event) => handleBalanceClick(event, customer)}
                        sx={{
                          cursor: "pointer",
                          fontWeight: 700,
                          fontSize: "0.8rem",
                          color: balance != null && balance < 0 ? "#d32f2f" : "inherit",
                          textDecoration: "underline !important",
                        }}
                      >
                        {formatPopupBalance(balance)}
                      </MuiLink>
                    </TableCell>
                  </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
      <DialogActions>
        <Button onClick={resetAndClose}>Close</Button>
      </DialogActions>
      <CustomerOrderHistoryPopup
        open={Boolean(historyCustomer)}
        customer={historyCustomer}
        onClose={() => setHistoryCustomer(null)}
      />
      <CustomerLedgerEnquiryPopup
        open={Boolean(ledgerCustomer)}
        customer={ledgerCustomer}
        onClose={() => setLedgerCustomer(null)}
      />
      {isTelecaller ? (
        <QuickOrderCustomerEditPopup
          open={Boolean(editCustomer)}
          customer={editCustomer}
          onClose={() => setEditCustomer(null)}
        />
      ) : null}
    </Dialog>
  );
};

export default CustomerListPopup;
